import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";

import CommonTable from "../../../components/Common/TableContainer";
import OnboardingDocumentAddEditModal, {
    INITIAL_ONBOARDING_DOCUMENT_FORM,
} from "pages/AllCommonModal/OnboardingDocumentAddEditModal/onboarding-document-add-edit-modal";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useOnboardingDocumentContext } from "libs/context/OnboardingDocumentContext";
import { submitOnboardingDocumentFilterData } from "model/OnboardingDocumentModel";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";

const unwrapOnboardingDocumentRecords = (payload) => {
    const root = payload?.data ?? payload;
    if (Array.isArray(root?.records)) return root.records;
    if (Array.isArray(root)) return root;
    if (Array.isArray(payload?.records)) return payload.records;
    return [];
};

const mapOnboardingDocumentRows = (records = []) =>
    (Array.isArray(records) ? records : []).map((item) => ({
        ...item,
        ID: item?.id,
    }));

const OnboardingDocuments = () => {
    const { permissionData } = usePermissionContext() || {};
    const { fetchOnboardingDocumentContextData, onboardingDocumentContextData } =
        useOnboardingDocumentContext() || {};
    const [onboardingDocumentAddEditModalOpen, setOnboardingDocumentAddEditModalOpen] =
        useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [onboardingDocumentData, setOnboardingDocumentData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [itemId, setItemId] = useState(null);
    const [tableMetaData, setTableMetadata] = useState();
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
    const [formData, setFormData] = useState(INITIAL_ONBOARDING_DOCUMENT_FORM);

    const filteredData = useMemo(() => {
        if (!searchQuery?.trim()) return onboardingDocumentData;

        return onboardingDocumentData?.filter((item) =>
            Object.values(item).some((val) =>
                String(val).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [onboardingDocumentData]);

    const onboardingDocumentActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.OnboardingDocumentsAccess),
            canEdit: Boolean(permissionData?.OnboardingDocumentsAccess),
        }),
        [permissionData]
    );

    const hasAnyOnboardingDocumentRowAction = useMemo(
        () => onboardingDocumentActionPermissions.canEdit,
        [onboardingDocumentActionPermissions]
    );

    const handleEdit = (row) => {
        setItemId(row?.id);
        setOnboardingDocumentAddEditModalOpen(true);
    };

    useEffect(() => {
        fetchOnboardingDocumentContextData?.();
        getOnboardingDocumentList();
    }, []);

    useEffect(() => {
        const valueReModified = onboardingDocumentContextData?.table?.columns?.map(
            (col) => ({
                Header: col?.title,
                accessor: col?.field,
            })
        );
        setTableMetadata(valueReModified);
    }, [onboardingDocumentContextData]);

    const getOnboardingDocumentList = async () => {
        setLoading(true);
        const response = await submitOnboardingDocumentFilterData({});
        const valueReModified = mapOnboardingDocumentRows(
            unwrapOnboardingDocumentRecords(response)
        );
        if (response) {
            setOnboardingDocumentData(valueReModified);
        } else {
            setOnboardingDocumentData([]);
        }
        setLoading(false);
    };

    const handleFilterSubmit = async (data) => {
        setLoading(true);
        const response = await submitOnboardingDocumentFilterData(data);
        const filterData = unwrapOnboardingDocumentRecords(response);
        if (response) {
            setOnboardingDocumentData(mapOnboardingDocumentRows(filterData));
        } else {
            setOnboardingDocumentData([]);
        }
        setLoading(false);
        setCommonFilterModalOpen(false);
    };

    const handleSearchSubmit = async (value) => {
        setLoading(true);
        const response = await submitOnboardingDocumentFilterData({ search: value });
        const searchData = unwrapOnboardingDocumentRecords(response);
        if (response) {
            setOnboardingDocumentData(mapOnboardingDocumentRows(searchData));
        } else {
            setOnboardingDocumentData([]);
        }
        setLoading(false);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Onboarding Documents</h5>

                                {onboardingDocumentActionPermissions.canCreate ? (
                                    <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                setItemId(null);
                                                setFormData(INITIAL_ONBOARDING_DOCUMENT_FORM);
                                                setOnboardingDocumentAddEditModalOpen(true);
                                            }}
                                        >
                                            + Create
                                        </Button>
                                    </Col>
                                ) : null}
                            </div>
                            <hr />

                            <Row className="align-items-center g-2 mb-4">
                                <Col xs="12" md="8">
                                    <div className="d-flex border rounded w-100 category-search-input">
                                        <Input
                                            type="text"
                                            placeholder="Search by name"
                                            className="border-0"
                                            value={searchQuery}
                                            onChange={(event) =>
                                                setSearchQuery(event?.target?.value)
                                            }
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    handleSearchSubmit(searchQuery?.trim());
                                                }
                                            }}
                                        />
                                        <Button
                                            className="search-icon-button"
                                            onClick={() =>
                                                handleSearchSubmit(searchQuery?.trim())
                                            }
                                        >
                                            <i className="fa fa-search" />
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {!loading &&
                            onboardingDocumentData?.length &&
                            tableMetaData?.length ? (
                                <CommonTable
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyOnboardingDocumentRowAction}
                                    onEdit={
                                        onboardingDocumentActionPermissions.canEdit
                                            ? handleEdit
                                            : undefined
                                    }
                                    isEditAccess={onboardingDocumentActionPermissions.canEdit}
                                    isCheckboxLeftSide={false}
                                    isPagination={false}
                                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                                    pageName={"Onboarding Documents"}
                                />
                            ) : (
                                <Loaders />
                            )}
                        </CardBody>
                    </Card>

                    <OnboardingDocumentAddEditModal
                        setOnboardingDocumentAddEditModalOpen={
                            setOnboardingDocumentAddEditModalOpen
                        }
                        onboardingDocumentAddEditModalOpen={
                            onboardingDocumentAddEditModalOpen
                        }
                        getOnboardingDocumentList={getOnboardingDocumentList}
                        itemId={itemId}
                        setFormData={setFormData}
                        formData={formData}
                    />
                    <AllCommonFilterModal
                        setCommonFilterModalOpen={setCommonFilterModalOpen}
                        commonFilterModalOpen={commonFilterModalOpen}
                        modalTitle="Onboarding Documents"
                        handleSubmit={handleFilterSubmit}
                        filterData={onboardingDocumentContextData?.filters}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

OnboardingDocuments.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(OnboardingDocuments);
